import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ExternalLink,
  Pin,
  CircleCheck,
  CircleAlert,
  Loader2,
  Clock,
  Rocket,
  History,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useMe,
  useRequestPreview,
  useVersionHistory,
  usePinVersion,
  type VersionHistoryRow,
} from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/publish')({
  staticData: { crumb: 'Publikacja' },
  component: PublishPage,
});

function PublishPage() {
  const { session } = Route.useRouteContext();
  if (session.role === 'operator') return null;

  const me = useMe();
  const history = useVersionHistory();
  const request = useRequestPreview();
  const pin = usePinVersion();
  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  const tenant = history.data?.tenant ?? me.data?.tenant ?? null;
  const versions = history.data?.versions ?? [];
  const previewHost = tenant?.preview_token
    ? `${tenant.preview_token}.pozyskajpacjenta.pl`
    : null;
  const liveHost =
    tenant?.custom_host ??
    (tenant?.slug ? `${tenant.slug}.pozyskajpacjenta.pl` : null);

  const onRequest = async () => {
    setFeedback(null);
    try {
      const r = (await request.mutateAsync()) as {
        requested_version?: number;
        preview_url?: string;
      };
      setFeedback({
        kind: 'ok',
        text: `Zlecono budowę wersji ${r.requested_version ?? ''}. Status zaktualizuje się tutaj automatycznie.`,
      });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  const onPin = async (version: number) => {
    setFeedback(null);
    try {
      await pin.mutateAsync(version);
      setFeedback({
        kind: 'ok',
        text: `Wersja ${version} jest teraz na żywo.`,
      });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  const pending = versions.some(
    (v) => v.status === 'requested' || v.status === 'building'
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            Publikacja
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Historia wszystkich budowań strony. Wybierz dowolną wersję i przypnij ją
            na żywo — bez czekania na ponowną generację.
          </p>
        </div>
        <Button onClick={onRequest} disabled={request.isPending || pending}>
          <Rocket className="size-4" aria-hidden="true" />
          {request.isPending
            ? 'Zlecanie…'
            : pending
            ? 'Trwa budowa…'
            : 'Zleć nową budowę'}
        </Button>
      </header>

      {feedback ? (
        <p
          role="status"
          className={cn(
            'rounded-md px-4 py-2 text-sm ring-1',
            feedback.kind === 'ok'
              ? 'bg-success/10 text-success ring-success/20'
              : 'bg-error/10 text-error ring-error/20'
          )}
        >
          {feedback.text}
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Live</CardDescription>
            <CardTitle className="text-base">
              {tenant?.live_version ? `Wersja ${tenant.live_version}` : 'Jeszcze nie opublikowano'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {liveHost && tenant?.live_version ? (
              <a
                href={`https://${liveHost}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
              >
                {liveHost}
                <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            ) : (
              <p className="text-xs text-text-muted">
                Po pierwszej publikacji strona pojawi się tutaj.
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Podgląd</CardDescription>
            <CardTitle className="text-base">
              {tenant?.preview_version
                ? `Wersja ${tenant.preview_version}`
                : 'Brak podglądu'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {previewHost && tenant?.preview_version ? (
              <a
                href={`https://${previewHost}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
              >
                {previewHost}
                <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            ) : (
              <p className="text-xs text-text-muted">
                Zleć budowę, aby zobaczyć podgląd przed publikacją.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="size-5" aria-hidden="true" /> Historia wersji
          </CardTitle>
          <CardDescription>
            Każdy wpis to jedna budowa Twojej strony. Strzałka „Na żywo"
            wskazuje obecnie aktywną wersję. Możesz przypiąć dowolną gotową wersję bez
            ponownego generowania.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.isPending ? (
            <p className="text-sm text-text-muted">Ładowanie…</p>
          ) : versions.length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Jeszcze nie zlecano budowy. Kliknij „Zleć nową budowę", aby wygenerować
              pierwszą wersję strony.
            </p>
          ) : (
            <ul role="list" className="space-y-2">
              {versions.map((v) => (
                <VersionRow
                  key={v.version}
                  version={v}
                  isLive={tenant?.live_version === v.version}
                  isPreview={tenant?.preview_version === v.version}
                  canPin={
                    v.status === 'ready' &&
                    tenant?.live_version !== v.version &&
                    !pin.isPending
                  }
                  onPin={() => onPin(v.version)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function VersionRow({
  version,
  isLive,
  isPreview,
  canPin,
  onPin,
}: {
  version: VersionHistoryRow;
  isLive: boolean;
  isPreview: boolean;
  canPin: boolean;
  onPin: () => void;
}) {
  const generatedAt = new Date(version.generated_at * 1000);
  const generatedLabel = generatedAt.toLocaleString('pl-PL', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="font-display text-base font-semibold text-text">
            Wersja {version.version}
          </p>
          <StatusBadge status={version.status} />
          {isLive ? (
            <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs text-success ring-1 ring-success/20">
              Na żywo
            </span>
          ) : null}
          {isPreview && !isLive ? (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent ring-1 ring-accent/20">
              Najnowszy podgląd
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-text-muted">
          <Clock className="mr-1 inline size-3" aria-hidden="true" />
          {generatedLabel}
          {' · '}
          <span className="tabular-nums">{version.page_count}</span> podstron
          {' · '}
          <span className="font-mono text-text-subtle">{version.brief_hash}</span>
          {version.generator ? <> · {version.generator}</> : null}
        </p>
        {version.error ? (
          <p className="mt-1 text-xs text-error">{version.error}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {canPin ? (
          <Button type="button" variant="secondary" size="sm" onClick={onPin}>
            <Pin className="size-3.5" aria-hidden="true" />
            Przypnij na żywo
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: VersionHistoryRow['status'] }) {
  const meta: Record<
    VersionHistoryRow['status'],
    { label: string; cls: string; Icon: React.ComponentType<{ className?: string }> }
  > = {
    requested: { label: 'Zlecono', cls: 'bg-surface text-text-muted ring-border', Icon: Clock },
    building: { label: 'Buduje', cls: 'bg-accent/10 text-accent ring-accent/20', Icon: Loader2 },
    ready: { label: 'Gotowa', cls: 'bg-success/10 text-success ring-success/20', Icon: CircleCheck },
    failed: { label: 'Błąd', cls: 'bg-error/10 text-error ring-error/20', Icon: CircleAlert },
  };
  const { label, cls, Icon } = meta[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1',
        cls
      )}
    >
      <Icon className={cn('size-3', status === 'building' && 'animate-spin')} aria-hidden="true" />
      {label}
    </span>
  );
}
